import { create } from 'zustand';
import { requestCheckout, type CheckoutLink } from '../lib/checkoutClient';
import { supabase } from '../lib/supabase';

export interface CreditPack {
  id: string;
  /** Per-locale pack name; `en` is the fallback for unlisted languages. */
  label: Record<string, string>;
  credits: number;
  priceJpy: number;
  priceUsd: number;
}

export const CREDIT_PACKS: CreditPack[] = [
  { id: 'credits_100',  label: { ja: 'スターター',   en: 'Starter',  zh: '入门' }, credits: 100,  priceJpy: 500,  priceUsd: 5 },
  { id: 'credits_300',  label: { ja: 'スタンダード', en: 'Standard', zh: '标准' }, credits: 300,  priceJpy: 1200, priceUsd: 12 },
  { id: 'credits_1000', label: { ja: 'プロ',         en: 'Pro',      zh: '专业' }, credits: 1000, priceJpy: 3500, priceUsd: 35 },
];

/** Pack name for a UI language, falling back to English. */
export function creditPackLabel(pack: CreditPack, lang: string): string {
  return pack.label[lang] ?? pack.label.en;
}

/** Bounded wait for the balance query — the Supabase client has no deadline. */
const BALANCE_FETCH_TIMEOUT_MS = 8_000;

interface CreditRow {
  balance: number | null;
  total_purchased: number | null;
  total_consumed: number | null;
}

/**
 * Bumped ONLY by `reset()` (sign-out / account change). A response from an
 * older epoch is discarded: without this, a `fetchBalance` already in flight
 * when the user signs out lands afterwards and writes the previous account's
 * balance back into a store that was just cleared.
 *
 * Deliberately not bumped per request: two panels fetching the same user
 * concurrently return the same number, and discarding the older one made its
 * caller believe it had a confirmed reading when the store had not been
 * written at all.
 */
let balanceEpoch = 0;
/**
 * Issued per request. Only the newest response writes the store, so a slow
 * earlier read cannot land after a newer one and restore a stale balance
 * (which, for the post-delivery Autotune debit, would mean handing out a paid
 * render). Callers that need certainty use the value `fetchBalance` RETURNS —
 * their own read — rather than depending on store write ordering.
 */
/** Account the store currently describes; pushed down from authStore so this
 *  module never has to import it back (authStore already imports this one). */
let activeUserId: string | null = null;
let balanceSeq = 0;
let latestBalanceSeq = 0;

function withTimeout<T>(p: Promise<T>, ms: number): Promise<T> {
  return Promise.race([
    p,
    new Promise<never>((_, reject) =>
      setTimeout(() => reject(new Error('credit balance lookup timed out')), ms),
    ),
  ]);
}

interface CreditState {
  balance: number;
  totalPurchased: number;
  totalConsumed: number;
  isLoading: boolean;
  /** True once a balance was actually read back. `balance` is only meaningful
   *  as "confirmed" when this is true — it starts at 0 for everyone. */
  hydrated: boolean;

  /** Resolves with the balance this call read. Rejects if it could not read one. */
  fetchBalance: (userId: string) => Promise<number>;
  buyCredits: (packId: string, currency: 'jpy' | 'usd', email?: string, source?: string) => Promise<CheckoutLink | null>;
  consumeCredits: (amount: number, reason: string, metadata?: Record<string, unknown>) => Promise<boolean>;
  /**
   * Commit a balance obtained outside `fetchBalance` (signup bonus, monthly Pro
   * grant). Account-scoped and sequence-stamped like a fetch, so a grant for a
   * user who has since signed out cannot land in the next user's store, and a
   * slower earlier fetch cannot overwrite it.
   */
  applyGrantedBalance: (userId: string, balance: number) => void;
  /** Called by authStore whenever the signed-in user changes (including to
   *  null). Resets only on an actual id change, so a token refresh does not
   *  wipe a good balance, but a direct account switch — which emits SIGNED_IN
   *  and no SIGNED_OUT — does. */
  setActiveUser: (userId: string | null) => void;
  reset: () => void;
}

export const useCreditStore = create<CreditState>((set) => ({
  balance: 0,
  totalPurchased: 0,
  totalConsumed: 0,
  isLoading: false,
  hydrated: false,

  /**
   * Load the balance. Supabase reports failures as `{ data: null, error }`
   * rather than by throwing, and the old code destructured only `data` — so a
   * flaky connection wrote `balance: 0` and looked exactly like a confirmed
   * empty wallet. Anything gated on the balance then told a paying user they
   * were out of credits. A failed lookup now keeps the previous value, leaves
   * `hydrated` false and rethrows, so callers can tell "confirmed zero" from
   * "could not find out".
   */
  fetchBalance: async (userId: string) => {
    // First read for an account also claims it, so a grant that arrives before
    // any fetch still passes the ownership check below.
    if (activeUserId !== userId) activeUserId = userId;
    const epoch = balanceEpoch;
    const seq = ++balanceSeq;
    set({ isLoading: true });
    try {
      const query = supabase
        .from('user_credits')
        .select('balance, total_purchased, total_consumed')
        .eq('user_id', userId)
        .maybeSingle();
      // The client has no deadline of its own; without one an awaiting caller
      // can hang forever on a dead connection.
      const { data, error } = await withTimeout(
        query as unknown as Promise<{ data: CreditRow | null; error: unknown }>,
        BALANCE_FETCH_TIMEOUT_MS,
      );
      if (error) throw error;
      // Signed out while this was in flight: drop the result rather than
      // repopulating a cleared store. The caller's catch is not triggered —
      // there was no error — but `hydrated` stays false, so nothing treats
      // the discarded read as a confirmed balance.
      if (epoch !== balanceEpoch) {
        throw new Error('credit balance lookup superseded by sign-out');
      }
      const balance = data?.balance ?? 0;
      // Out-of-order response: the caller still gets its own reading below,
      // but the shared store keeps the newer one.
      if (seq >= latestBalanceSeq) {
        latestBalanceSeq = seq;
        set({
          balance,
          totalPurchased: data?.total_purchased ?? 0,
          totalConsumed: data?.total_consumed ?? 0,
          isLoading: false,
          hydrated: true,
        });
      } else {
        set({ isLoading: false });
      }
      return balance;
    } catch (e) {
      if (epoch === balanceEpoch) set({ isLoading: false });
      throw e;
    }
  },

  buyCredits: async (packId, currency, email, source = 'unknown') =>
    requestCheckout('/api/stripe/create-credit-checkout', { packId, currency, source, ...(email ? { email } : {}) }),

  consumeCredits: async (amount, reason, metadata = {}) => {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.access_token) return false;

      const res = await fetch('/api/credits/consume', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${session.access_token}`,
        },
        body: JSON.stringify({ amount, reason, metadata }),
      });

      if (!res.ok) return false;
      const data = await res.json() as { balance?: number; totalConsumed?: number };
      set((state) => ({
        balance: data.balance ?? Math.max(0, state.balance - amount),
        totalConsumed: data.totalConsumed ?? state.totalConsumed + amount,
      }));
      return true;
    } catch {
      return false;
    }
  },

  applyGrantedBalance: (userId, balance) => {
    if (activeUserId !== userId) return;
    latestBalanceSeq = ++balanceSeq;
    set({ balance, isLoading: false, hydrated: true });
  },

  setActiveUser: (userId) => {
    if (activeUserId === userId) return;
    activeUserId = userId;
    balanceEpoch += 1;
    latestBalanceSeq = balanceSeq;
    set({ balance: 0, totalPurchased: 0, totalConsumed: 0, isLoading: false, hydrated: false });
  },

  reset: () => {
    activeUserId = null;
    balanceEpoch += 1;
    latestBalanceSeq = balanceSeq;
    set({ balance: 0, totalPurchased: 0, totalConsumed: 0, isLoading: false, hydrated: false });
  },
}));
